import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import { Card, CardContent } from "@nimit9/signet-ui/components/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CheckCircle2, Loader2, Ticket } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { z } from "zod"
import { PaisaMark } from "@/components/common/paisa-mark"
import { api } from "@/lib/api"
import { ApiError, AuthError } from "@/lib/api-errors"

export const Route = createFileRoute("/join")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: JoinPage,
})

function JoinPage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const [state, setState] = useState<"loading" | "success" | "error" | "no-token">(
    token ? "loading" : "no-token",
  )
  const [errorMessage, setErrorMessage] = useState<string>("")

  const [confirmMessage, setConfirmMessage] = useState<string | null>(null)

  const accept = useCallback(
    (confirmLeave: boolean) => {
      if (!token) return
      setState("loading")
      api
        .post("/households/invites/accept", confirmLeave ? { token, confirmLeave } : { token })
        .then(() => {
          setState("success")
          setTimeout(() => navigate({ to: "/" }), 1500)
        })
        .catch((err) => {
          if (err instanceof AuthError) {
            // Not logged in — redirect to login, then back here
            window.location.href = `/login?redirect=/join?token=${encodeURIComponent(token)}`
            return
          }
          if (err instanceof ApiError && err.status === 409 && !confirmLeave) {
            // Caller is in another household with other members — ask before moving them.
            setConfirmMessage(err.message)
            return
          }
          setState("error")
          setErrorMessage(err instanceof Error ? err.message : "Failed to accept invite")
        })
    },
    [token, navigate],
  )

  useEffect(() => {
    accept(false)
  }, [accept])

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg-base px-4 py-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-green/10">
          <PaisaMark size={30} decorative />
        </span>

        <Card className="w-full" data-testid="join-card" data-state={state}>
          <CardContent className="space-y-4 p-6 text-center sm:p-8">
            <div className="space-y-2">
              {token && (
                <Badge variant="secondary" className="mx-auto gap-1.5">
                  <Ticket aria-hidden="true" className="h-3 w-3" />
                  Household invite
                </Badge>
              )}
              <h1 className="text-title font-semibold tracking-tight text-text-primary">
                Join household
              </h1>
            </div>

            {state === "loading" && (
              <div role="status" className="space-y-3">
                <Loader2 aria-hidden="true" className="mx-auto h-5 w-5 animate-spin text-primary" />
                <p className="text-body text-text-muted">Accepting invite...</p>
              </div>
            )}

            {state === "success" && (
              <div role="status" className="space-y-3">
                <CheckCircle2 aria-hidden="true" className="mx-auto h-6 w-6 text-primary" />
                <p className="text-body text-text-muted">
                  You've joined the household. Redirecting...
                </p>
              </div>
            )}

            {state === "error" && (
              <>
                <p role="alert" className="text-body text-destructive">
                  {errorMessage}
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/" })}>
                  Go home
                </Button>
              </>
            )}

            {state === "no-token" && (
              <>
                <p className="text-body text-text-muted">
                  This invite link is incomplete. Ask for a new one.
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/" })}>
                  Go home
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={confirmMessage !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmMessage(null)
            setState("error")
            setErrorMessage("You chose not to leave your current household.")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave your current household?</DialogTitle>
            <DialogDescription>{confirmMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmMessage(null)
                setState("error")
                setErrorMessage("You chose not to leave your current household.")
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmMessage(null)
                accept(true)
              }}
            >
              Leave and join
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
